// src/components/NewsEditorModal.jsx
import React, { useMemo, useRef, useState, useEffect } from 'react';
import ns from './News.module.css';
import { apiFetch } from '../utils/auth';
import { API_URL } from '../config';
import { ReactComponent as UploadNewsPictureIcon } from '../icons/upload_chat_picture.svg';
import { Input, MultiSelect, Modal } from './UI';

// === Tiptap ===
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle, FontSize, Color } from '@tiptap/extension-text-style';
import DOMPurify from 'dompurify';
import { HexColorPicker } from 'react-colorful';

// Локальный резолвер относительных путей картинок в абсолютные
const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
const resolveImageUrl = (p) =>
  !p ? '' : /^https?:\/\//i.test(p) ? p : `${API_BASE}${p}`;

/**
 * props:
 * - mode: 'create' | 'edit'
 * - item:  существующая новость (для режима edit)
 * - rubrics: [{id, name}] — список рубрик
 * - onClose: () => void
 * - onSaved: (savedItem) => void   // вызывается после успешного сохранения
 */
export default function NewsEditorModal({ mode = 'create', item = null, rubrics = [], onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const rubricPickerRef = useRef(null);

  // --- поля формы
  const [title, setTitle] = useState(item?.title ?? '');
  // content теперь будет HTML из редактора
  const [content, setContent] = useState(item?.content ?? '');
  const [dateStr] = useState(item?.date ?? '');
  const [isMain, setIsMain] = useState(Boolean(item?.is_main));
  const [isTop, setIsTop] = useState(Boolean(item?.is_top));
  const [rubricIds, setRubricIds] = useState(
    item?.rubrics?.map(r => r.id) ?? []
  );

  // тик для мгновенного ререндера тулбара при смене выделения/сторэд-марк
  const [toolbarTick, setToolbarTick] = useState(0);

  // цветовой пикер
  const [pickerOpen, setPickerOpen] = useState(false);       // открыт ли поповер
  const [customColor, setCustomColor] = useState('#BD23DB'); // текущий выбранный цвет
  const pickerRef = useRef(null);                            // для клика вне

  const promptAndSetLink = (editorInstance) => {
    if (!editorInstance) return;
    const url = window.prompt('Вставьте ссылку:');
    if (url === null) return;
    if (url === '') {
      editorInstance.chain().focus().unsetLink().run();
      return;
    }
    editorInstance
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url, target: '_blank', rel: 'noopener nofollow' })
      .run();
  };

  // --- Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit,              // bold/italic/strike/paragraph/list/undo/redo
      Underline,
      Link.configure({ openOnClick: false }),
      TextStyle,               // для инлайн-стилей
      FontSize,
      Color,                   // цвет текста
    ],
    content: item?.content || '',
    editorProps: {
      attributes: { class: ns.richEditor },
      handleKeyDown(view, event) {
        const isMod = event.ctrlKey || event.metaKey;
        if (!isMod) return false;

        const key = event.key.toLowerCase();
        const chain = view?.editor?.chain().focus();
        if (!chain) return false;

        if (key === 'b') {
          event.preventDefault();
          chain.toggleBold().run();
          return true;
        }
        if (key === 'i') {
          event.preventDefault();
          chain.toggleItalic().run();
          return true;
        }
        if (key === 'u') {
          event.preventDefault();
          chain.toggleUnderline().run();
          return true;
        }
        if (key === 'k') {
          event.preventDefault();
          promptAndSetLink(view.editor);
          return true;
        }

        return false;
      },
    },
    onUpdate({ editor }) {
      setContent(editor.getHTML()); // ререндер при изменении документа
      setToolbarTick(t => t + 1);
    },
    onSelectionUpdate() {
      setToolbarTick(t => t + 1);   // ререндер при смене выделения / stored marks
    },
    onTransaction() {
      // будет вызвано и при переключении stored marks
      setToolbarTick(t => t + 1);
    },
  });

  const run = (fn) => editor && editor.chain().focus() && fn(editor.chain().focus());

  const setLink = () => promptAndSetLink(editor);

  // === Управление размером шрифта ===
  const FONT_MIN = 12;
  const FONT_MAX = 35;
  const FONT_STEP = 2;

  function getCurrentFontSizePx() {
    const v = editor?.getAttributes('textStyle')?.fontSize || '';
    const num = parseInt(String(v).replace('px', ''), 10);
    return Number.isFinite(num) ? num : 16; // базовый дефолт
  }

  function applyFontSize(px) {
    if (!editor) return;
    const clamped = Math.max(FONT_MIN, Math.min(FONT_MAX, px));
    editor.chain().focus().setFontSize(`${clamped}px`).run(); // команда из FontSize
    setToolbarTick(t => t + 1);
  }

  function incFont() {
    applyFontSize(getCurrentFontSizePx() + FONT_STEP);
  }
  function decFont() {
    applyFontSize(getCurrentFontSizePx() - FONT_STEP);
  }

  // === Цвет текста ===
  const setTextColor = (hex) => editor?.chain().focus().setColor(hex).run();
  const clearTextColor = () => editor?.chain().focus().unsetColor().run();

  // выбор через колесо
  const onPickColor = (hex) => {
    setCustomColor(hex);
    editor?.chain().focus().setColor(hex).run();
    setToolbarTick(t => t + 1);
  };

  // --- картинки
  const [imageFile, setImageFile] = useState(null);                 // горизонтальная 16:9
  const [imageVerticalFile, setImageVerticalFile] = useState(null); // вертикальная 9:16
  const [busy, setBusy] = useState(false);
  const canSave = title.trim() && content.trim() && !busy;

  const imagePreview = useMemo(() => {
    if (imageFile) return URL.createObjectURL(imageFile);
    return resolveImageUrl(item?.image_url) || '';
  }, [imageFile, item]);

  const imageVerticalPreview = useMemo(() => {
    if (imageVerticalFile) return URL.createObjectURL(imageVerticalFile);
    return resolveImageUrl(item?.vertical_image_url) || '';
  }, [imageVerticalFile, item]);

  // показывать ли под картинками поле выбора рубрик (как в рейтингах)
  const [rubricFieldVisible, setRubricFieldVisible] = useState(
    (item?.rubrics?.length ?? 0) > 0
  );

  useEffect(() => {
    if (!pickerOpen) return;
    const onDocClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [pickerOpen]);

  const handleSubmit = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      // Санитизируем HTML перед отправкой (разрешаем span/style для font-size и color)
      const safeContent = DOMPurify.sanitize(content, {
        ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'ul', 'ol', 'li', 'a', 'span'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'style']
      });

      let saved = null;

      if (!isEdit) {
        // ---- CREATE (multipart)
        const fd = new FormData();
        fd.append('title', title);
        fd.append('content', safeContent);
        if (dateStr) fd.append('date_str', dateStr);
        if (typeof isMain === 'boolean') fd.append('is_main', String(isMain));
        if (typeof isTop === 'boolean') fd.append('is_top', String(isTop));
        rubricIds.forEach(id => fd.append('rubric_ids', String(id)));
        if (imageFile) fd.append('image', imageFile);
        if (imageVerticalFile) fd.append('image_vertical', imageVerticalFile);

        const res = await apiFetch(`${API_URL}/news`, {
          method: 'POST',
          body: fd,
        });
        saved = await res.json();
      } else {
        // ---- EDIT (JSON + (опционально) отдельные загрузки картинок)
        // 1) патчим основные поля
        const patchBody = {
          title,
          content: safeContent,
          date: dateStr || null,
          rubric_ids: rubricIds,
          is_main: isMain,
          is_top: isTop,
        };
        const res = await apiFetch(`${API_URL}/news/${item.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patchBody),
        });
        saved = await res.json();

        // 2) если выбрали новые изображения — загрузим по спец. эндпоинтам
        if (imageFile) {
          const fdImg = new FormData();
          fdImg.append('image', imageFile);
          const r = await apiFetch(`${API_URL}/news/${item.id}/image`, { method: 'POST', body: fdImg });
          saved = await r.json();
        }
        if (imageVerticalFile) {
          const fdV = new FormData();
          fdV.append('image_vertical', imageVerticalFile);
          const r2 = await apiFetch(`${API_URL}/news/${item.id}/image_vertical`, { method: 'POST', body: fdV });
          saved = await r2.json();
        }
      }

      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      console.error(e);
      alert('Ошибка при сохранении новости.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Редактировать новость' : 'Добавить новость'}
      onClose={onClose}
      onCancel={onClose}
      cancelDisabled={busy}
      onSubmit={handleSubmit}
      submitLabel={isEdit ? 'Сохранить' : 'Создать'}
      submitDisabled={!canSave}
      className={ns.editorBackdrop}
      bodyClassName={ns.editorForm}
    >
      <div className={ns.editorGrid}>
        <div className={ns.fieldHeader}>
          <label className={ns.editorLabel}>Название <span style={{ color: "rgb(192, 16, 72)", fontWeight: "400" }}>*</span></label>
        </div>
        <Input
          className={ns.editorInput}
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          ariaLabel="Название"
        />

        <div className={ns.fieldHeader}>
          <label className={ns.editorLabel}>Текст новости <span style={{ color: "rgb(192, 16, 72)", fontWeight: "400" }}>*</span></label>
        </div>

        {/* === Тулбар форматирования === */}
        <div className={ns.editorToolbar}>
          <button
            type="button"
            className={editor?.isActive('bold') ? ns.isActive : ''}
            onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleBold().run(); }}
            title="Жирный (Ctrl/Cmd+B)"
          ><b>B</b></button>

          <button
            type="button"
            className={editor?.isActive('italic') ? ns.isActive : ''}
            onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleItalic().run(); }}
            title="Курсив (Ctrl/Cmd+I)"
          ><i>I</i></button>

          <button
            type="button"
            className={editor?.isActive('underline') ? ns.isActive : ''}
            onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleUnderline().run(); }}
            title="Подчёркнутый (Ctrl/Cmd+U)"
          ><u>U</u></button>

          <button
            type="button"
            className={editor?.isActive('strike') ? ns.isActive : ''}
            onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleStrike().run(); }}
            title="Зачёркнутый"
          ><s>S</s></button>

          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().undo().run(); }}
            title="Отменить"
          >↶</button>

          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().redo().run(); }}
            title="Повторить"
          >↷</button>

          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setLink(); }}
            title="Вставить ссылку"
          >🔗</button>

          {/* Размер шрифта */}
          <button
            type="button"
            className={editor?.isActive('textStyle') ? 'isActive' : ''} // подсветка рамки
            onMouseDown={(e) => { e.preventDefault(); decFont(); }}
            title="Уменьшить размер шрифта"
          >Ａ−</button>

          <button
            type="button"
            className={editor?.isActive('textStyle') ? 'isActive' : ''}
            onMouseDown={(e) => { e.preventDefault(); incFont(); }}
            title="Увеличить размер шрифта"
          >Ａ+</button>

          {/* Цвет текста: свотчи + сброс */}
          {/* Цвет текста: только сброс + палитра */}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); clearTextColor(); }}
            title="Сбросить цвет"
          >✕</button>

          {/* Триггер палитры — НЕ кнопка, увеличен размер */}
          <div className={ns.colorPickerWrap}>
            <span
              role="button"
              tabIndex={0}
              onMouseDown={(e) => { e.preventDefault(); setPickerOpen(v => !v); }}
              title="Выбрать любой цвет"
              className={ns.paletteTrigger}
            >🎨</span>

            {pickerOpen && (
              <div ref={pickerRef} className={ns.colorPopover}>
                <HexColorPicker color={customColor} onChange={onPickColor} />
              </div>
            )}
          </div>
        </div>

        {/* === Поле редактора === */}
        <EditorContent editor={editor} />

        {/* Капсулы под дизайн: Сделать главной / ТОП / Рубрика */}
        <div className={ns.segRow}>
          <button
            type="button"
            className={`${ns.segBtn} ${isMain ? ns.segBtnActive : ''}`}
            onClick={() => setIsMain(v => !v)}
          >
            Сделать главной
          </button>
          <button
            type="button"
            className={`${ns.segBtn} ${isTop ? ns.segBtnActive : ''}`}
            onClick={() => setIsTop(v => !v)}
          >
            ТОП
          </button>
          <button
            type="button"
            className={`${ns.segBtn} ${rubricFieldVisible ? ns.segBtnActive : ''}`}
            onClick={() => {
              setRubricFieldVisible(prev => {
                const next = !prev;
                if (!next) {
                  setRubricIds([]);
                } else {
                  // при активации — прокручиваем к полю
                  setTimeout(() => rubricPickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                }
                return next;
              });
            }}
            title="Выбрать рубрику"
          >
            Рубрика
          </button>
        </div>

        {/* Горизонтальная 16:9 */}
        <div className={ns.uploadRow}>
          <div className={ns.uploadCol}>
            <div className={ns.editorLabel} style={{ fontSize: "13px" }}>Изображение горизонталь 16:9</div>
            <label className={ns.uploadBtn}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                style={{ display: 'none' }}
              />
              <UploadNewsPictureIcon className={ns.uploadIcon} aria-hidden="true" />
            </label>
          </div>
          <div className={ns.previewCol}>
            {imagePreview ? (
              <img className={ns.imagePreview} src={imagePreview} alt="" />
            ) : (
              <div className={ns.previewPh} />
            )}
          </div>
        </div>

        {/* Вертикальная 9:16 — показывать только если выбран ТОП или есть рубрика */}
        {(isTop || rubricFieldVisible || rubricIds.length > 0) && (
          <div className={ns.uploadRow}>
            <div className={ns.uploadCol}>
              <div className={ns.editorLabel} style={{ fontSize: "13px" }}>Изображение вертикаль 9:16</div>
              <label className={ns.uploadBtn}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageVerticalFile(e.target.files?.[0] || null)}
                  style={{ display: 'none' }}
                />
                <UploadNewsPictureIcon className={ns.uploadIcon} aria-hidden="true" />
              </label>
            </div>
            <div className={ns.previewCol}>
              {imageVerticalPreview ? (
                <img className={ns.imagePreviewVertical} src={imageVerticalPreview} alt="" />
              ) : (
                <div className={ns.previewPhVertical} />
              )}
            </div>
          </div>
        )}
      </div>
      {/* Поле выбора рубрик — как в рейтингах (только триггерит окно) */}
      {rubricFieldVisible && (
        <div className={ns.rubricField}>
          <div className={ns.fieldHeader}>
            <label className={ns.editorLabel}>Рубрика</label>
            <span className={ns.reqMuted}>можно выбрать несколько</span>
          </div>
          {rubricIds.length > 0 && (
            <div className={ns.selectedTags}>
              {rubrics.filter((r) => rubricIds.includes(r.id)).map((r) => (
                <span key={r.id} className={ns.tag}>
                  {r.name}
                  <button
                    type="button"
                    className={ns.tagRemove}
                    aria-label={`Убрать ${r.name}`}
                    onClick={() => setRubricIds((prev) => prev.filter((x) => x !== r.id))}
                  >
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M2 2l8 8M10 2l-8 8" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
          <MultiSelect
            className={ns.rubricMultiSelect}
            value={rubricIds}
            onChange={setRubricIds}
            placeholder="Выберите рубрику"
            emptyMessage="Нет доступных рубрик"
            ariaLabel="Рубрика"
          >
            {rubrics.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </MultiSelect>


        </div>
      )}
    </Modal>
  );
}
